import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NewSelect } from "../..";
import { UndefinedOptions, useGroupOptions, useFeedcurveOptions, useMilestoneOptions, useSectionOptions } from "../../_Components/";
import { colors, fonts, glyphs, useMediaQuery } from "../../../Styles";
import { ActionField, NewFieldStyles, quickEntryChange, ValueType, ActionContent, ActionDisplayEnum, ActionDisplayType, DataObj, QuickEntry, ValueSteps } from "../../../Definitions";
import { RootState, useAppSelector } from "../../../Store";
import { selectAllFeedCurveMilestoneOptions, selectMilestoneOptionsByFeedCurve } from "../../../Store/OptionsHandlers/optionsCombinedReducer";
import { isEqual } from "lodash";

type QuickEntryModalLvl2Props = {
  quickEntryChange: quickEntryChange[];
  quickEntryData: QuickEntry[];
  onQuickEntryChange: (data: quickEntryChange[]) => void;
  focus_view?: boolean
};

const labelToKeyMap: Record<string, "feedCurveId" | "animal_group_id" | "milestone_id" | "milestone_day" | "location_id" | "reset_consumption" | "correction"> = {
  feedCurve: "feedCurveId",
  group: "animal_group_id",
  milestone: "milestone_id",
  milestone_day: "milestone_day",
  location_id: "location_id",
  reset_consumption: "reset_consumption",
  feedCorrection: "correction",
};

export default function QuickEntryModalLvl2({ quickEntryChange, quickEntryData, onQuickEntryChange, focus_view = false }: QuickEntryModalLvl2Props) {
  const { t } = useTranslation()

  const groupOptions = useGroupOptions(UndefinedOptions.nan);
  const { feedCurveOptions } = useFeedcurveOptions(UndefinedOptions.nan);
  const milestoneOptions = useMilestoneOptions();
  const { locationOptions } = useSectionOptions();

  const [selectFields, setSelectFields] = useState<ActionField[]>([{ id: -1 }]);
  const mobileXs = useMediaQuery(`(max-width: 793px)`) || focus_view;

  const feedCurveMilestones = useAppSelector(selectAllFeedCurveMilestoneOptions).reduce((agg, milestone) => {
    const { feedCurveId } = milestone;
    agg[feedCurveId] = agg[feedCurveId] || []
    agg[feedCurveId].push(milestone)
    return agg;
  }, {} as { [key: number]: (ValueType & { feedCurveId: number; index: number; milestoneId: number; })[] });

  const changedFeedCurveId = quickEntryChange.find((entry) => {
    return entry.key === 'feedCurveId'
  })?.value?.[0].id;

  const selectedFeedCurveIdsFromFilter = quickEntryData.find((entry) => (entry.key === 'feedCurveId'))
  const feedCurveMilestonesFromSelected = selectedFeedCurveIdsFromFilter?.value.flatMap(({ id }) => feedCurveMilestones[id]);
  const milestoneOptionFromChange = useAppSelector((state: RootState) => { if (changedFeedCurveId !== undefined) { return selectMilestoneOptionsByFeedCurve(state, [changedFeedCurveId]) } }, isEqual);

  const filteredOptions = milestoneOptions.filter((option) => {
    if (selectedFeedCurveIdsFromFilter === undefined) {
      return true;
    }
    return feedCurveMilestonesFromSelected?.map((value) => value.milestoneId).includes(option.id)
  })

  // #region | Action Parameters
  const actionParameters: ActionDisplayType[] = [
    // Group
    { label: "group", id: 1, displayType: ActionDisplayEnum.select, options: groupOptions.filter(option => option.id !== undefined) as { label: string; id: number }[] },

    // Feed Curve
    { label: "feedCurve", id: 2, displayType: ActionDisplayEnum.select, options: feedCurveOptions?.filter(option => option.id !== undefined) as { label: string; id: number }[] },

    // Milestones
    { label: "milestone", id: 3, displayType: ActionDisplayEnum.select, options: milestoneOptionFromChange ? milestoneOptionFromChange : filteredOptions, },

    // Milestone day
    { label: "milestone_day", id: 4, displayType: ActionDisplayEnum.input },

    // Condition
    // {label: "condition", id: 4, displayType: ActionDisplayEnum.select, options: conditionOptions},

    // Birth Date
    // {label: "birthdate", id: 5, displayType: ActionDisplayEnum.date},

    // Feed Correction
    { label: "feedCorrection", id: 6, displayType: ActionDisplayEnum.input, minValueNumber: -99, maxValueNumber: 100, valueStep: ValueSteps.integers },

    // Feed Skip
    // {label: "birthdate", id: 7, displayType: ActionDisplayEnum.custom},

    // Move
    { label: "location_id", id: 8, displayType: ActionDisplayEnum.select, options: locationOptions.filter(option => option.id !== undefined) as { label: string; id: number }[] },

    // Remove
    // {label: "remove", id 9}

    // Reset consumption
    { label: "reset_consumption", id: 10 },
  ]

  const actionParamOptions = actionParameters.map((type) => {
    let translationField = `params.${type.label}`;
    if (type.label === "feedCorrection") {
      translationField = "feed.feedCorrection.feedCorrection";
    }
    return {
      label: t(translationField, { defaultValue: type.label }),
      id: type.id,
      length: 0
    }
  });
  // #endregion

  // If quickEntryChange already have values to show
  useEffect(() => {
    if (quickEntryChange && quickEntryChange.length > 0) {
      const updatedFields: ActionField[] = quickEntryChange.map((param) => {
        const label = Object.entries(labelToKeyMap).find(([_, key]) => key === param.key)?.[0];
        const id = actionParameters.find((p) => p.label === label)?.id ?? -1;
        return { id, value: param.value };
      });
      updatedFields.push({ id: -1 });
      setSelectFields(updatedFields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickEntryChange]);

  // Handle filter type change
  const handleFilterTypeChange = (index: number, value: { id: number }[]) => {
    const updatedSelectFields = [...selectFields];
    if (value.length > 0) {
      if (updatedSelectFields[index].id === -1) {
        updatedSelectFields.push({ id: -1 });
      }
      updatedSelectFields[index] = { id: value[0]?.id ? +value[0].id : 0, value: undefined };

      const selectedParameter = actionParameters.find((param) => param.id === value[0]?.id);
      if (selectedParameter) {
        const updatedFilterParameter = Array.isArray(quickEntryChange) ? [...quickEntryChange] : [];
        const mappedKey = labelToKeyMap[selectedParameter.label];
        if (mappedKey) {
          updatedFilterParameter[index] = {
            key: mappedKey as unknown as DataObj,
            value: mappedKey === "reset_consumption" ? Math.floor(Date.now() / 1000) : undefined
          };
          onQuickEntryChange(updatedFilterParameter);
        }
      }
    } else {
      updatedSelectFields[index].id = undefined;
    }
    setSelectFields(updatedSelectFields);
  };

  // Handle filter value change
  const handleFilterValueChange = (index: number, value: ValueType) => {
    const updatedFilterParameter = Array.isArray(quickEntryChange) ? [...quickEntryChange] : [];

    if (!updatedFilterParameter[index]) return;
    updatedFilterParameter[index].value = value;
    onQuickEntryChange(updatedFilterParameter);
  };

  // Remove a SelectField by index
  const handleRemoveField = useCallback((index: number) => {
    const updatedSelectFields = [...selectFields];
    updatedSelectFields.splice(index, 1);
    setSelectFields(updatedSelectFields);

    const updatedFilterParameter = quickEntryChange.filter((_, i) => i !== index);

    onQuickEntryChange(updatedFilterParameter);
  }, [onQuickEntryChange, quickEntryChange, selectFields]);

  // #region | Rendered Fields
  const renderedFields = selectFields.map((field, index) => {

    // Remove already used options from optionsList
    const selectedIds = selectFields
      .filter((f, i) => i !== index && f.id !== undefined)
      .map((f) => f.id);
    const availableOptions = actionParamOptions.filter((option) => !selectedIds.includes(option.id));

    return (
      <div key={index} style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        marginBottom: index === selectFields.length - 1 ? "0px" : "30px",
        paddingBottom: focus_view ? index === selectFields.length - 1 ? "none" : "10px" : "none",
        borderBottom: focus_view ? index === selectFields.length - 1 ? "none" : "1px dashed " + colors.greyDarkPrimary : "none"
      }}>
        <div style={{ display: "grid", gridTemplateColumns: mobileXs ? "1fr 30px" : "280px 280px 25px", gridAutoRows: "1fr", gap: "10px", width: "100%" }}>
          <div style={{ gridArea: "1/1/2/2" }}>
            <NewSelect
              header={field.id !== -1 ? t("change", { defaultValue: "change" }) : ""}
              placeholder={t("select", { defaultValue: "select" })}
              key={index + String(field.id)}
              options={availableOptions}
              onChange={(option) => {
                option?.id && handleFilterTypeChange(index, [{ id: Number(option.id) }]);
              }}
              valueOverrides={{
                defaultValue: actionParamOptions.find(option => option.id === field.id)
              }}
              fieldOverrides={{
                fieldWidth: mobileXs ? "250px" : "280px",
                fieldStyle: focus_view ? NewFieldStyles.greyDarkPrimary : NewFieldStyles.blueDarkSecondary,
                focus_view: focus_view
              }}
            />
            {field.id === 10 &&
              <div
                style={{
                  ...fonts.paragraph,
                  ...fonts.fontSize4,
                  color: colors.whitePrimary
                }}
              >
                {t("animalPages.animals.resetConsumptionDisclaimer")}
              </div>
            }
          </div>
          {/* Content based on selected filter parameter */}
          {field.id !== -1 && (
            <div style={{ gridArea: mobileXs ? "2/1/3/3" : "1/2/2/3" }}>
              <ActionContent
                filter={{ ...field, value: Array.isArray(field.value) ? field.value[0] : field.value }}
                index={index}
                filterParameterTypes={actionParameters}
                handleFilterValueChange={(values) => { handleFilterValueChange(index, values) }}
                focus_view={focus_view}
              />
            </div>
          )}
          {field.id !== -1 && (
            <div style={{ gridArea: mobileXs ? "1/2/2/3" : "1/3/2/4", display: "flex", alignItems: "end", justifyContent: "end" }}>
              <div style={{ cursor: "pointer" }} onClick={() => handleRemoveField(index)}>
                <glyphs.Delete size={focus_view ? "30px" : "25px"} />
              </div>
            </div>
          )}
        </div>
      </div>
    )
  })
  // #endregion

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "start",
      flexDirection: "column",
      boxSizing: "border-box"
    }}>
      {renderedFields}
    </div>
  )
}
